import { useEffect, useState } from 'react';

import { useAppSelector } from 'dux/app/hooks';

import clone from 'lodash/clone';
import filter from 'lodash/filter';
import remove from 'lodash/remove';

import { legacyTheme, styled } from '@prose-ui/legacy';

import Card from 'Components/Card';
import { Typography } from 'Components/Typography';

import type { ConsultationCategoryProp } from 'PropTypes/consultationProps';

import QuestionLayout from '../Scenes/QuestionLayout';

import * as consultationSelectors from 'dux/consultation/selectors';

const OptionsGroupWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-top: 16px;
  ${legacyTheme.breakpoints.up('md')} {
    width: 50%;
    margin-top: 40px;
  }
`;

const ProductLabel = styled(Typography)`
  margin-bottom: 8px;
`;

const OptionGroupWrapper = styled.div`
  margin-bottom: 24px;
  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 40px;
  }
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

type Option = {
  value: string;
  label: string;
  answersGroupSlug?: string;
};

type AnswerGroup = {
  title: string;
  slug: string;
};

type Question = {
  tip?: {
    title: string;
    content: string;
  };
  title: string;
  subtitle?: string;
  options: Option[];
  answersGroup: AnswerGroup[];
};

type RoutineProductsProps = {
  consultationCategory: ConsultationCategoryProp;
  consultationSubsetCategory: string;
  initialValue?: string[];
  questionIndex: number;
  onNext: (answers: string[]) => void;
  questionSet: { options: { showSteps: boolean } };
};

export const RoutineProducts = ({
  consultationCategory,
  initialValue = [],
  questionIndex,
  onNext,
  consultationSubsetCategory,
  questionSet,
}: RoutineProductsProps) => {
  const question: Question = useAppSelector((state) =>
    consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      questionIndex,
      consultationSubsetCategory,
    }),
  );

  const { options, subtitle, title, tip, answersGroup } = question;

  const [answerList, setAnswerList] = useState<string[]>(initialValue);

  useEffect(() => {
    setAnswerList(initialValue || []);
  }, [initialValue]);

  const onClickOption = (value: string) => {
    const updatedList = clone(answerList);
    if (!answerList.includes(value)) {
      updatedList.push(value);
    } else {
      remove(updatedList, (v) => v === value);
    }
    setAnswerList(updatedList);
  };

  const submit = () => onNext(answerList);

  return (
    <QuestionLayout
      consultationCategory={consultationCategory}
      disabled={false}
      display="flex"
      justifyContent="start"
      questionIndex={questionIndex}
      // @ts-expect-error dux not migrated yet
      questionSet={questionSet}
      showProgressTracker={!questionSet?.options?.showSteps}
      showSteps={questionSet?.options?.showSteps ?? false}
      submit={submit}
      subtitle={subtitle}
      tipContent={tip?.content}
      title={title}
    >
      <OptionsGroupWrapper>
        {answersGroup?.map((group) => {
          const optionsByGroup = filter(options, { answersGroupSlug: group.slug });
          return (
            <OptionGroupWrapper key={group.title}>
              <ProductLabel bold variant="mono1">
                {group.title}
              </ProductLabel>
              <ChipWrapper>
                {optionsByGroup.map(({ value, label }) => (
                  <Card
                    key={value}
                    chipMode
                    label={label}
                    onClick={() => onClickOption(value)}
                    selectable
                    selected={Boolean(answerList?.includes(value))}
                    theme="consultation"
                  />
                ))}
              </ChipWrapper>
            </OptionGroupWrapper>
          );
        })}
      </OptionsGroupWrapper>
    </QuestionLayout>
  );
};
